import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { T } from "../../tokens";

// ── Admin Inventory Control Center (v1) ──────────────────────────────────────
//
// Operational clarity, not visual polish. The operator pastes a controlled-scan
// DRY-RUN summary; the server (read-only) returns the governed apply decision plus
// live safety state (scoring frozen? queue idle? embeddings confirmed?). NOTHING is
// scanned, applied, or scored — this panel only reads and decides.

type Decision = "AUTO_APPLY_ALLOWED" | "APPLY_NARROWED" | "DO_NOT_APPLY" | "NEEDS_ADMIN_APPROVAL" | "NEEDS_SOURCE_FIX";

interface Payload {
  decision: Decision;
  autoApplyAllowed: boolean;
  adminApprovalRequired: boolean;
  blockingReasons: string[];
  warnings: string[];
  recommendedFilters: {
    acceptedOnly: boolean; excludeNeedsReview: boolean;
    familyFilter?: string | null; maxPerCompany?: number | null;
    excludeSource?: string | null; indiaRemoteOnly?: boolean;
  };
  targetFamily: string | null;
  targetFamilyReadiness: { family: string; usableActive: number; sufficient: boolean } | null;
  perFamilyInventory: Array<{ family: string; usableActive: number; sufficient: boolean }>;
  concentration: { company: { topShare: number; topName: string | null }; source: { topShare: number; topName: string | null } };
  poolHealth: { totalJobs: number; activeJobs: number; activeEmbedded: number; activeMissingEmbedding: number };
  safetyRequirements: {
    scoringFrozenNow: boolean; scoreQueueEmptyNow: boolean; embeddingConfirmedNow: boolean;
  };
  dryRunSummary: { fetched: number; accepted: number; needsReview: number; rejected: number; acceptRatePct: number };
}

const DECISION_COLOR: Record<Decision, string> = {
  AUTO_APPLY_ALLOWED: T.ok,
  APPLY_NARROWED: T.teal,
  NEEDS_ADMIN_APPROVAL: T.tealSoft ?? T.teal,
  NEEDS_SOURCE_FIX: T.err,
  DO_NOT_APPLY: T.err,
};

function num(v: string): number { const n = Number(v); return Number.isFinite(n) ? n : 0; }

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: T.s1, border: `1px solid ${T.b0}`, borderRadius: "10px", padding: "14px 18px", marginBottom: "12px" }}>
      <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3, letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "8px" }}>{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value, accent }: { label: string; value: React.ReactNode; accent?: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${T.b0}` }}>
      <span style={{ fontFamily: T.body, fontSize: "12px", color: T.t2 }}>{label}</span>
      <span style={{ fontFamily: T.mono, fontSize: "13px", fontWeight: 600, color: accent ?? T.t0 }}>{value}</span>
    </div>
  );
}

function Check({ ok, label }: { ok: boolean; label: string }) {
  return <Row label={label} value={ok ? "✓ ok" : "✗ blocked"} accent={ok ? T.ok : T.err} />;
}

// ── Pool coverage (8a/8b): jobs-in-pool vs users-targeting, per family ────────
interface CoverageRow { family: string; label: string; jobCount: number; userCount: number; gap: "none" | "thin" | "severe"; }
interface CoveragePayload {
  rows: CoverageRow[]; gaps: CoverageRow[];
  totals: { families: number; jobsClassified: number; usersClassified: number; unknownJobs: number; unknownUsers: number; gapFamilies: number; severeGapFamilies: number };
  generatedAt: string;
}
const GAP_COLOR: Record<CoverageRow["gap"], string> = { severe: T.err, thin: T.tealSoft ?? T.teal, none: T.t3 };
const GAP_TEXT:  Record<CoverageRow["gap"], string> = { severe: "GAP", thin: "thin", none: "ok" };

// ── Targeted family scan (8c): the "Scan for this family" gap-filler ──────────
interface FamilyRun {
  family: string; fetched: number; familyMatched: number; accepted: number;
  needsReview: number; rejected: number; newJobs: number; status: string; finishedAt: string;
}
interface FamilyRunsPayload { runs: FamilyRun[]; generatedAt: string }
interface ScanResponse { enqueued: boolean; status: string; reason?: string; family: string; keywords?: string[] }

function PoolCoverageCard() {
  const q = useQuery({
    queryKey: ["admin", "pool-coverage"],
    queryFn: async (): Promise<CoveragePayload> => (await axios.get<CoveragePayload>("/api/admin/pool-coverage")).data,
  });
  // Latest targeted-scan result per family (populated once a queued scan finishes).
  const runsQ = useQuery({
    queryKey: ["admin", "scan", "family-runs"],
    queryFn: async (): Promise<FamilyRunsPayload> => (await axios.get<FamilyRunsPayload>("/api/admin/scan/family-runs")).data,
    refetchInterval: 15_000,   // a queued scan lands asynchronously
  });
  const [notice, setNotice] = useState<{ family: string; text: string; ok: boolean } | null>(null);
  const scan = useMutation({
    mutationFn: async (family: string): Promise<ScanResponse> =>
      (await axios.post<ScanResponse>("/api/admin/scan", { family })).data,
    onSuccess: (d) => { setNotice({ family: d.family, text: "Gated scan queued — result appears below when it finishes.", ok: true }); void runsQ.refetch(); },
    onError: (e: unknown) => {
      const ax = e as { response?: { data?: { reason?: string; status?: string } } };
      const reason = ax.response?.data?.reason ?? ax.response?.data?.status ?? "scan_failed";
      const family = scan.variables ?? "";
      const hint = reason === "admin_scan_trigger_disabled" ? "feature off (ADMIN_SCAN_TRIGGER_ENABLED)"
        : reason === "scoring_not_frozen" ? "scoring is live — freeze it first"
        : reason;
      setNotice({ family, text: `Refused: ${hint}`, ok: false });
    },
  });

  const latestByFamily = new Map<string, FamilyRun>();
  for (const r of runsQ.data?.runs ?? []) if (!latestByFamily.has(r.family)) latestByFamily.set(r.family, r);

  const th: React.CSSProperties = { fontFamily: T.mono, fontSize: "9px", color: T.t3, textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "left", padding: "4px 8px", borderBottom: `1px solid ${T.b1}` };
  const td: React.CSSProperties = { fontFamily: T.mono, fontSize: "12px", color: T.t1, padding: "5px 8px", borderBottom: `1px solid ${T.b0}` };
  const btn: React.CSSProperties = { fontFamily: T.mono, fontSize: "10px", padding: "3px 8px", borderRadius: "5px", border: `1px solid ${T.b1}`, background: T.s2, color: T.teal, cursor: "pointer" };

  return (
    <Card title="Pool coverage — jobs in pool vs users targeting (scan gaps)">
      {q.isLoading && <span style={{ fontFamily: T.body, fontSize: "12px", color: T.t3 }}>Loading coverage…</span>}
      {q.isError && <span style={{ fontFamily: T.body, fontSize: "12px", color: T.err }}>Failed to load coverage.</span>}
      {q.data && (
        <>
          <p style={{ fontFamily: T.body, fontSize: "12px", color: q.data.totals.gapFamilies > 0 ? T.err : T.t2, marginTop: 0 }}>
            {q.data.totals.gapFamilies === 0
              ? "No under-served families — every family with users has enough jobs."
              : `${q.data.totals.gapFamilies} under-served famil${q.data.totals.gapFamilies === 1 ? "y" : "ies"} (${q.data.totals.severeGapFamilies} with 0 jobs). Scan a gap to fetch → gate → embed passing jobs.`}
          </p>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>Family</th><th style={{ ...th, textAlign: "right" }}>Jobs</th><th style={{ ...th, textAlign: "right" }}>Users</th><th style={{ ...th, textAlign: "right" }}>Status</th><th style={{ ...th, textAlign: "left" }}>Scan</th></tr></thead>
              <tbody>
                {q.data.rows.map((r) => {
                  const run = latestByFamily.get(r.family);
                  const pending = scan.isPending && scan.variables === r.family;
                  return (
                    <tr key={r.family}>
                      <td style={{ ...td, color: r.gap === "severe" ? T.err : T.t0 }}>{r.label}</td>
                      <td style={{ ...td, textAlign: "right" }}>{r.jobCount}</td>
                      <td style={{ ...td, textAlign: "right" }}>{r.userCount}</td>
                      <td style={{ ...td, textAlign: "right", color: GAP_COLOR[r.gap], fontWeight: r.gap === "severe" ? 700 : 400 }}>{GAP_TEXT[r.gap]}</td>
                      <td style={td}>
                        {r.gap !== "none" ? (
                          <button style={{ ...btn, opacity: pending ? 0.5 : 1 }} disabled={pending} onClick={() => scan.mutate(r.family)}>
                            {pending ? "queuing…" : "Scan for this family"}
                          </button>
                        ) : <span style={{ color: T.t3 }}>—</span>}
                        {notice?.family === r.family && (
                          <span style={{ marginLeft: "8px", fontSize: "10px", color: notice.ok ? T.teal : T.err }}>{notice.text}</span>
                        )}
                        {run && (
                          <span style={{ marginLeft: "8px", fontSize: "10px", color: T.t2 }}>
                            last: {run.familyMatched} matched · <span style={{ color: T.ok }}>{run.accepted} accepted</span> · <span style={{ color: T.tealSoft ?? T.teal }}>{run.needsReview} review</span> · <span style={{ color: T.err }}>{run.rejected} rejected</span> · {run.newJobs} new
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ fontFamily: T.mono, fontSize: "10px", color: T.t3, marginTop: "8px" }}>
            {q.data.totals.usersClassified} users · {q.data.totals.jobsClassified} classified jobs
            {q.data.totals.unknownUsers > 0 ? ` · ${q.data.totals.unknownUsers} users unclassified` : ""}
            {q.data.totals.unknownJobs > 0 ? ` · ${q.data.totals.unknownJobs} jobs unclassified` : ""}
            {" · scan = fetch → admission gate → embed (no scoring)"}
          </div>
        </>
      )}
    </Card>
  );
}

export function AdminInventoryControl() {
  // Prefilled with the real DEV dry-run shape so the panel is meaningful on first load.
  const [form, setForm] = useState({
    targetFamily: "qa_sdet",
    targetFamilyUsableCount: "0",
    fetched: "732", accepted: "35", needsReview: "369", rejected: "328",
    companyTopShare: "0.35", companyTopName: "MongoDB",
    sourceTopShare: "0.7", sourceTopName: "greenhouse",
  });
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });

  const m = useMutation({
    mutationFn: async (): Promise<Payload> => {
      const { data } = await axios.post<Payload>("/api/admin/scan-decision", {
        targetFamily: form.targetFamily || null,
        targetFamilyUsableCount: num(form.targetFamilyUsableCount),
        dryRun: { fetched: num(form.fetched), accepted: num(form.accepted), needsReview: num(form.needsReview), rejected: num(form.rejected) },
        companyConcentration: { topShare: num(form.companyTopShare), topName: form.companyTopName || null },
        sourceConcentration: { topShare: num(form.sourceTopShare), topName: form.sourceTopName || null },
      });
      return data;
    },
  });
  const p = m.data;

  const inputStyle = { padding: "6px 8px", background: T.s2, border: `1px solid ${T.b1}`, borderRadius: "5px", color: T.t0, fontFamily: T.mono, fontSize: "12px", width: "100%" as const };
  const field = (label: string, k: keyof typeof form) => (
    <label style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
      <span style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</span>
      <input value={form[k]} onChange={set(k)} style={inputStyle} />
    </label>
  );

  return (
    <div style={{ maxWidth: "820px" }}>
      <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, marginTop: 0 }}>
        Turn a <code>controlled-scan</code> dry-run into a governed apply decision. Read-only — nothing is
        scanned, applied, or scored. Matching/scoring must stay <strong>paused</strong> during a broad scan
        or inventory refresh.
      </p>

      <PoolCoverageCard />

      <Card title="Scan dry-run summary (paste from controlled-scan --json)">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px" }}>
          {field("target family", "targetFamily")}
          {field("useful for target", "targetFamilyUsableCount")}
          {field("fetched", "fetched")}
          {field("accepted", "accepted")}
          {field("needs_review", "needsReview")}
          {field("rejected", "rejected")}
          {field("top company share", "companyTopShare")}
          {field("top company", "companyTopName")}
          {field("top source share", "sourceTopShare")}
          {field("top source", "sourceTopName")}
        </div>
        <button
          type="button" onClick={() => m.mutate()} disabled={m.isPending}
          style={{ marginTop: "12px", padding: "8px 16px", background: T.teal, border: "none", borderRadius: "6px",
            color: "#04120f", fontFamily: T.body, fontSize: "12px", fontWeight: 700, cursor: "pointer" }}
        >
          {m.isPending ? "Evaluating…" : "Evaluate decision"}
        </button>
        {m.isError && <p style={{ color: T.err, fontFamily: T.body, fontSize: "12px" }}>Failed — admin access required, or check inputs.</p>}
      </Card>

      {p && (
        <>
          <div style={{ background: T.s1, border: `1px solid ${DECISION_COLOR[p.decision]}`, borderRadius: "10px", padding: "16px 20px", marginBottom: "12px" }}>
            <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3, letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "4px" }}>Decision</div>
            <div style={{ fontFamily: T.display, fontSize: "22px", fontWeight: 700, color: DECISION_COLOR[p.decision] }}>{p.decision}</div>
            <div style={{ display: "flex", gap: "16px", marginTop: "8px" }}>
              <span style={{ fontFamily: T.mono, fontSize: "12px", color: p.autoApplyAllowed ? T.ok : T.err }}>
                auto-apply: {p.autoApplyAllowed ? "ALLOWED" : "BLOCKED"}
              </span>
              <span style={{ fontFamily: T.mono, fontSize: "12px", color: T.t2 }}>
                admin approval: {p.adminApprovalRequired ? "required" : "n/a"}
              </span>
            </div>
          </div>

          <Card title="Scoring / queue / embedding safety (must all pass to auto-apply)">
            <Check ok={p.safetyRequirements.scoringFrozenNow} label="Scoring frozen (SCORE_JOBS_ENABLED=false)" />
            <Check ok={p.safetyRequirements.scoreQueueEmptyNow} label="Score queue idle (waiting+active+delayed = 0)" />
            <Check ok={p.safetyRequirements.embeddingConfirmedNow} label="Embedding availability confirmed" />
          </Card>

          {p.blockingReasons.length > 0 && (
            <Card title="Why auto-apply is blocked">
              {p.blockingReasons.map((r) => <div key={r} style={{ fontFamily: T.mono, fontSize: "12px", color: T.err, padding: "3px 0" }}>• {r}</div>)}
            </Card>
          )}

          {p.warnings.length > 0 && (
            <Card title="Warnings">
              {p.warnings.map((w, i) => <div key={i} style={{ fontFamily: T.body, fontSize: "12px", color: T.t1, padding: "3px 0" }}>⚠ {w}</div>)}
            </Card>
          )}

          <Card title="Recommended narrowed apply plan">
            <Row label="accepted-only (exclude needs_review)" value={p.recommendedFilters.excludeNeedsReview ? "yes" : "no"} accent={T.ok} />
            {p.recommendedFilters.familyFilter && <Row label="family filter" value={p.recommendedFilters.familyFilter} />}
            {p.recommendedFilters.maxPerCompany != null && <Row label="max per company" value={p.recommendedFilters.maxPerCompany} />}
            {p.recommendedFilters.excludeSource && <Row label="exclude source" value={p.recommendedFilters.excludeSource} accent={T.err} />}
            {p.recommendedFilters.indiaRemoteOnly && <Row label="india/remote only" value="yes" />}
          </Card>

          <Card title="Target family readiness">
            {p.targetFamilyReadiness
              ? <Row label={p.targetFamilyReadiness.family} value={`${p.targetFamilyReadiness.usableActive} usable`} accent={p.targetFamilyReadiness.sufficient ? T.ok : T.err} />
              : <span style={{ fontFamily: T.body, fontSize: "12px", color: T.t3 }}>No target family set.</span>}
          </Card>

          {p.perFamilyInventory.length > 0 && (
            <Card title="Per-role inventory sufficiency">
              {p.perFamilyInventory.map((f) => <Row key={f.family} label={f.family} value={`${f.usableActive} usable`} accent={f.sufficient ? T.ok : T.err} />)}
            </Card>
          )}

          <Card title="Job pool health">
            <Row label="Total jobs" value={p.poolHealth.totalJobs} />
            <Row label="Active jobs" value={p.poolHealth.activeJobs} />
            <Row label="Active embedded" value={p.poolHealth.activeEmbedded} accent={T.ok} />
            <Row label="Active MISSING embedding" value={p.poolHealth.activeMissingEmbedding} accent={T.err} />
          </Card>
        </>
      )}
    </div>
  );
}
